/**
 * Editorial Workflow Service (NWFE-RQ-01)
 * API calls for the two-stage editorial review of generated questions.
 */

import axios from '../utils/axios';

const API_URL = '/editorial-workflow';

// ── Types ───────────────────────────────────────────────────────────────────

export type EditorialTaskState =
  | 'pending_stage_1'
  | 'pending_stage_2'
  | 'reviewed'
  | 'rejected'
  | 'published';

export interface TaskAssignee {
  userId: string;
  name: string;
  email?: string;
  assignedBy: string;
  assignedAt: string;
}

export interface ReviewTask {
  _id: string;
  questionId: string;
  state: EditorialTaskState;
  questionSummary: {
    stem: string;
    oeCode: string;
    type: string;
    difficulty: string;
    isVersion: boolean;
  };
  // Client request (Carlos): identify which client/request a task belongs to
  questionPortfolios?: string[];
  questionTaxonomyPaths?: string[];
  stage1Assignee?: TaskAssignee | null;
  stage2Assignee?: TaskAssignee | null;
  stage1Observations?: string;
  stage1CompletedAt?: string;
  stage1CompletedByName?: string;
  stage2CompletedAt?: string;
  stage2CompletedByName?: string;
  rejectedAtStage?: 1 | 2;
  rejectionCategory?: string;
  rejectionComment?: string;
  salesforceRecordId?: string;
  createdAt: string;
  updatedAt: string;
}

export interface ReviewTaskDetail extends ReviewTask {
  question: any; // full pdf_generated_questions document
}

export interface TaskTransition {
  _id: string;
  taskId: string;
  questionId: string;
  fromState: EditorialTaskState | null;
  toState: EditorialTaskState;
  action: 'transition' | 'assignment';
  byUserId: string;
  byUserName: string;
  at: string;
  meta?: Record<string, any>;
}

export interface RejectionCategory {
  key: string;
  label: string;
  description: string;
}

export interface WorkflowConfig {
  states: EditorialTaskState[];
  rejectionCategories: RejectionCategory[];
}

export interface TaskListResult {
  tasks: ReviewTask[];
  counts: Record<string, number>;
  pagination: { page: number; limit: number; total: number; totalPages: number };
}

export interface Reviewer {
  _id: string;
  name: string;
  email: string;
  role: 'linguist' | 'professor';
}

export interface StageVersionContent {
  stem: string;
  explanation: string;
  correctAnswer?: string;
  alternatives?: Array<{ letter: string; text: string; isCorrect?: boolean }>;
  initialContext?: string;
  caseContext?: string;
}

export interface StageVersion {
  _id: string;
  taskId: string;
  questionId: string;
  stage: 0 | 1 | 2;
  label: 'original' | 'post_stage_1' | 'post_stage_2';
  content: StageVersionContent;
  createdBy: string;
  createdByName: string;
  createdAt: string;
}

/** Reviewer edits sent on stage completion (HTML fields) */
export interface EditedQuestionPayload {
  stem?: string;
  explanation?: string;
  correctAnswer?: string;
  alternatives?: Array<{ letter: string; text: string }>;
  initialContext?: string;
  caseContext?: string;
}

export interface RejectionAnalyticsItem {
  taskId: string;
  questionId: string;
  oeCode: string;
  type: string;
  stemPreview: string;
  category: string;
  comment: string;
  rejectedAtStage: 1 | 2 | null;
  rejectedAt: string;
}

export interface RejectionAnalytics {
  total: number;
  byCategory: Record<string, number>;
  byStage: Record<string, number>;
  items: RejectionAnalyticsItem[];
}

export interface TaskListFilters {
  state?: string;
  oeCode?: string;
  type?: string;
  difficulty?: string;
  portfolio?: string; // base-scope parity with the Question Bank
  taxonomyNode?: string;
  search?: string;
  mine?: boolean;
  page?: number;
  limit?: number;
}

// ── Service ─────────────────────────────────────────────────────────────────

class EditorialWorkflowService {
  private getAuthHeaders() {
    const token = localStorage.getItem('accessToken');
    return { Authorization: `Bearer ${token}` };
  }

  async getConfig(): Promise<WorkflowConfig> {
    const response = await axios.get(`${API_URL}/config`, {
      headers: this.getAuthHeaders(),
    });
    return response.data.data;
  }

  async listTasks(filters: TaskListFilters = {}): Promise<TaskListResult> {
    const params: Record<string, string | number | boolean> = {};
    if (filters.state) params.state = filters.state;
    if (filters.oeCode) params.oeCode = filters.oeCode;
    if (filters.type) params.type = filters.type;
    if (filters.difficulty) params.difficulty = filters.difficulty;
    if (filters.portfolio) params.portfolio = filters.portfolio;
    if (filters.taxonomyNode) params.taxonomyNode = filters.taxonomyNode;
    if (filters.search) params.search = filters.search;
    if (filters.mine) params.mine = 'true';
    if (filters.page) params.page = filters.page;
    if (filters.limit) params.limit = filters.limit;

    const response = await axios.get(`${API_URL}/tasks`, {
      headers: this.getAuthHeaders(),
      params,
    });
    return response.data.data;
  }

  /** Export review tasks (XLSX/CSV) honoring the same filters as the list —
   * includes a dedicated Correct Answer column (client request). */
  async exportTasks(filters: TaskListFilters = {}, format: 'xlsx' | 'csv' = 'xlsx'): Promise<Blob> {
    const params: Record<string, string | boolean> = { format };
    if (filters.state) params.state = filters.state;
    if (filters.oeCode) params.oeCode = filters.oeCode;
    if (filters.type) params.type = filters.type;
    if (filters.difficulty) params.difficulty = filters.difficulty;
    if (filters.portfolio) params.portfolio = filters.portfolio;
    if (filters.taxonomyNode) params.taxonomyNode = filters.taxonomyNode;
    if (filters.search) params.search = filters.search;
    if (filters.mine) params.mine = 'true';

    const response = await axios.get(`${API_URL}/tasks/export`, {
      headers: this.getAuthHeaders(),
      params,
      responseType: 'blob',
    });
    return response.data;
  }

  async getTask(taskId: string): Promise<ReviewTaskDetail> {
    const response = await axios.get(`${API_URL}/tasks/${taskId}`, {
      headers: this.getAuthHeaders(),
    });
    return response.data.data;
  }

  async getTaskHistory(taskId: string): Promise<TaskTransition[]> {
    const response = await axios.get(`${API_URL}/tasks/${taskId}/history`, {
      headers: this.getAuthHeaders(),
    });
    return response.data.data;
  }

  async assignTask(taskId: string, stage: 1 | 2, userId: string): Promise<ReviewTask> {
    const response = await axios.post(
      `${API_URL}/tasks/${taskId}/assign`,
      { stage, userId },
      { headers: this.getAuthHeaders() }
    );
    return response.data.data;
  }

  async completeStage1(
    taskId: string,
    observations?: string,
    editedQuestion?: EditedQuestionPayload
  ): Promise<ReviewTask> {
    const response = await axios.post(
      `${API_URL}/tasks/${taskId}/stage-1/complete`,
      { observations, editedQuestion },
      { headers: this.getAuthHeaders() }
    );
    return response.data.data;
  }

  async completeStage2(
    taskId: string,
    editedQuestion?: EditedQuestionPayload
  ): Promise<ReviewTask> {
    const response = await axios.post(
      `${API_URL}/tasks/${taskId}/stage-2/complete`,
      { editedQuestion },
      { headers: this.getAuthHeaders() }
    );
    return response.data.data;
  }

  async getTaskVersions(taskId: string): Promise<StageVersion[]> {
    const response = await axios.get(`${API_URL}/tasks/${taskId}/versions`, {
      headers: this.getAuthHeaders(),
    });
    return response.data.data;
  }

  async rejectTask(taskId: string, category: string, comment?: string): Promise<ReviewTask> {
    const response = await axios.post(
      `${API_URL}/tasks/${taskId}/reject`,
      { category, comment },
      { headers: this.getAuthHeaders() }
    );
    return response.data.data;
  }

  async listReviewers(stage?: 1 | 2): Promise<Reviewer[]> {
    const response = await axios.get(`${API_URL}/reviewers`, {
      headers: this.getAuthHeaders(),
      params: stage ? { stage } : {},
    });
    return response.data.data;
  }

  async backfillTasks(): Promise<{ scanned: number; created: number }> {
    const response = await axios.post(
      `${API_URL}/tasks/backfill`,
      {},
      { headers: this.getAuthHeaders() }
    );
    return response.data.data;
  }

  async rejectionAnalytics(filters: { from?: string; to?: string } = {}): Promise<RejectionAnalytics> {
    const response = await axios.get(`${API_URL}/rejections`, {
      headers: this.getAuthHeaders(),
      params: filters,
    });
    return response.data.data;
  }

  /** One-time: make all existing questions visible in the bank; gate only new ones */
  async legacyReset(): Promise<{ tasksRemoved: number; questionsUnlocked: number }> {
    const response = await axios.post(
      `${API_URL}/tasks/legacy-reset`,
      {},
      { headers: this.getAuthHeaders() }
    );
    return response.data.data;
  }
}

export const editorialWorkflowService = new EditorialWorkflowService();
